/**
 * Renders a saved dashboard natively, with refresh and export.
 *
 * Replaces the Superset embed. Because the charts are drawn in this document
 * rather than inside a cross-origin frame, three things become possible that
 * were not before: a per-panel spinner and a per-panel retry, an export that
 * captures the exact pixels on screen, and a refresh that re-executes the
 * stored semantic query instead of reloading a frame we do not control.
 */
import {
  AlertTriangle,
  CalendarClock,
  ChartColumnBig,
  ChartLine,
  ChartPie,
  Download,
  FileText,
  Hash,
  Presentation,
  RefreshCw,
  Table2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  ApiError,
  dashboardsApi,
  type ExportFormat,
  type ExportMode,
} from "../services/api";
import { humanise } from "../services/chartOptions";
import { captureCharts } from "../services/chartRegistry";
import { devTrace } from "../services/devTrace";
import {
  dashboardEndorsementLabel,
  dashboardPeriodNotes,
} from "../services/governanceNotes";
import type { ChartType, Dashboard, Panel, PanelResult } from "../types";
import {
  dashboardEmailDocument,
  EmailButton,
  pdfAttachment,
  type EmailDraftSource,
} from "../features/email";
import { ChartView } from "./ChartView";

/**
 * What kind of panel this is, said in a glyph.
 *
 * The header used to carry a chip reading "bar". That is how the panel is
 * drawn, which the reader can already see, and it spent the one piece of
 * header furniture on the least useful thing it could say.
 */
const PANEL_ICONS: Record<ChartType, LucideIcon> = {
  bar: ChartColumnBig,
  line: ChartLine,
  pie: ChartPie,
  scalar: Hash,
  table: Table2,
};

interface DashboardViewProps {
  dashboard: Dashboard;
  /** Preview rows that arrived with the composed dashboard, if any. */
  initialResults?: PanelResult[];
  onRenamed?: (dashboard: Dashboard) => void;
}

/** What each export audience produces, in the reader's terms. */
const AUDIENCES: { mode: ExportMode; label: string; detail: string }[] = [
  {
    mode: "executive",
    label: "Executive summary",
    detail: "Headline figures and up to four charts. No detail tables.",
  },
  {
    mode: "operational",
    label: "Full detail",
    detail: "Every panel, detail tables, one page each.",
  },
];

/**
 * Asks which audience an export is for, at the moment it is exported.
 *
 * This replaced a standing dropdown in the toolbar. The choice only ever shaped
 * the exported file and changed nothing on screen, so sitting permanently beside
 * Refresh it read as a view control and invited the question of what it was for.
 */
export function ExportAudienceChooser({
  format,
  onChoose,
  onCancel,
}: {
  format: ExportFormat;
  onChoose: (mode: ExportMode) => void;
  onCancel: () => void;
}) {
  // Escape closes, matching every other transient surface in the product. The
  // listener is registered once per open rather than per render.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="export-audience"
      role="menu"
      aria-label={`${format === "pdf" ? "PDF" : "PowerPoint"} detail`}
    >
      {AUDIENCES.map((audience, index) => (
        <button
          key={audience.mode}
          type="button"
          role="menuitem"
          autoFocus={index === 0}
          onClick={() => onChoose(audience.mode)}
        >
          <span className="export-audience-label">{audience.label}</span>
          <span className="export-audience-detail">{audience.detail}</span>
        </button>
      ))}
      <button type="button" className="export-audience-cancel" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

/** Figures older than this are old enough to be worth refreshing before use. */
const STALE_AFTER_MS = 60 * 60 * 1000;

function isStale(iso: string): boolean {
  const elapsed = Date.now() - new Date(iso).getTime();
  return Number.isFinite(elapsed) && elapsed > STALE_AFTER_MS;
}

function relativeTime(iso: string): string {
  const elapsed = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(elapsed) || elapsed < 0) return "just now";
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function DashboardPanel({
  panel,
  result,
  busy,
  onRetry,
}: {
  panel: Panel;
  result: PanelResult | undefined;
  busy: boolean;
  onRetry: () => void;
}) {
  const failed = result?.status === "error";
  const Icon = PANEL_ICONS[panel.chartType];
  return (
    <section
      className={`dashboard-panel panel-${panel.chartType}`}
      // The height as well as the width. The server plans a height per kind of
      // panel — three rows for a headline figure, six for a chart, eight for a
      // table — and only the width was being read, so every panel came out the
      // same height and a KPI band was as tall as the charts it summarised.
      //
      // It is carried as a minimum rather than a row span: a span would clip a
      // narrative or an expanded provenance block, and a floor gets the same
      // proportions while letting a panel grow when its content needs it.
      style={
        {
          gridColumn: `span ${Math.min(panel.layout.w, 12)}`,
          "--panel-rows": Math.max(panel.layout.h, 1),
        } as CSSProperties
      }
      aria-busy={busy}
    >
      <header className="dashboard-panel-head">
        <span className="dashboard-panel-icon" aria-hidden="true">
          <Icon size={14} />
        </span>
        <h3>{panel.title}</h3>
        <div className="dashboard-panel-tags">
          {result?.truncated && (
            <span className="panel-chip" title="The governed row cap applied">
              capped
            </span>
          )}
          {/* Retrying one tile beats re-running a dashboard where only one
              panel failed, which is the whole point of per-panel results. */}
          {failed && (
            <button
              type="button"
              className="icon-button"
              onClick={onRetry}
              disabled={busy}
              aria-label={`Retry ${panel.title}`}
            >
              <RefreshCw size={15} className={busy ? "spinning" : ""} />
            </button>
          )}
        </div>
      </header>

      <ChartView
        panelId={panel.id}
        chartType={panel.chartType}
        encoding={panel.encoding}
        result={result}
        loading={busy}
      />

      {panel.narrative && (
        <p className="dashboard-panel-note">{panel.narrative}</p>
      )}

      {result?.provenance && (
        <details className="panel-provenance">
          <summary>How this was produced</summary>
          <dl>
            <dt>Metrics</dt>
            <dd>{panel.query.metrics.map(humanise).join(", ") || "—"}</dd>
            <dt>Dimensions</dt>
            <dd>{panel.query.dimensions.map(humanise).join(", ") || "—"}</dd>
            <dt>Rows</dt>
            <dd>{result.rowCount.toLocaleString()}</dd>
          </dl>
          {/* The semantic query is the definition; SQL is a derived artifact
              the context layer reports only when it can, so its absence is
              normal rather than a fault. */}
          {result.provenance.sourceSql.length > 0 && (
            <pre className="panel-sql">
              {result.provenance.sourceSql.join("\n\n")}
            </pre>
          )}
        </details>
      )}
    </section>
  );
}

export function DashboardView({
  dashboard,
  initialResults,
  onRenamed,
}: DashboardViewProps) {
  const [results, setResults] = useState<Map<string, PanelResult>>(
    () => new Map((initialResults ?? []).map((item) => [item.panelId, item])),
  );
  const [busyPanels, setBusyPanels] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState<ExportFormat | "">("");
  // The format whose audience is being chosen. The choice is asked for at export
  // time rather than kept as a standing control, because it changes nothing on
  // screen and a toolbar dropdown gave no hint that it only shaped the file.
  const [choosingFor, setChoosingFor] = useState<ExportFormat | "">("");
  const [notice, setNotice] = useState("");
  const [refreshedAt, setRefreshedAt] = useState<string>("");

  const panelIds = useMemo(
    () => dashboard.panels.map((panel) => panel.id),
    [dashboard.panels],
  );

  const applyResults = useCallback((incoming: PanelResult[]) => {
    setResults((current) => {
      const next = new Map(current);
      for (const result of incoming) next.set(result.panelId, result);
      return next;
    });
  }, []);

  const refresh = useCallback(
    async (targets: string[] = []) => {
      const scope = targets.length > 0 ? targets : panelIds;
      if (scope.length === 0) return;

      setNotice("");
      if (targets.length > 0) {
        setBusyPanels((current) => new Set([...current, ...targets]));
      } else {
        setRefreshing(true);
      }

      try {
        const fresh = await dashboardsApi.refresh(dashboard.id, targets);
        applyResults(fresh);
        setRefreshedAt(new Date().toISOString());

        const failed = fresh.filter((item) => item.status === "error");
        devTrace.record({
          runId: dashboard.id,
          step: "dashboard_refreshed",
          actor: "Dashboard",
          level: failed.length > 0 ? "warning" : "success",
          summary:
            failed.length > 0
              ? `Refreshed ${fresh.length} panels; ${failed.length} failed.`
              : `Refreshed ${fresh.length} panels.`,
          output: { dashboardId: dashboard.id, failed: failed.length },
        });
        if (failed.length > 0) {
          setNotice(
            `${failed.length} of ${fresh.length} panels could not be refreshed.`,
          );
        }
      } catch (error) {
        const message =
          error instanceof ApiError
            ? error.message
            : "The dashboard could not be refreshed.";
        setNotice(message);
        devTrace.record({
          runId: dashboard.id,
          step: "dashboard_refresh_failed",
          actor: "Dashboard",
          level: "error",
          summary: message,
        });
      } finally {
        setRefreshing(false);
        setBusyPanels((current) => {
          const next = new Set(current);
          for (const id of targets) next.delete(id);
          return next;
        });
      }
    },
    [dashboard.id, panelIds, applyResults],
  );

  // Cached results first, so the dashboard paints without waiting on the
  // engine. Any panel that has neither a streamed preview nor a cached result
  // has never run -- typically because its preview query failed -- and is
  // executed now, so it shows its real outcome instead of looking empty.
  const seededIds = useRef(
    new Set((initialResults ?? []).map((item) => item.panelId)),
  );
  useEffect(() => {
    let cancelled = false;
    const runMissing = (have: Set<string>) => {
      const missing = panelIds.filter((id) => !have.has(id));
      if (missing.length > 0) void refresh(missing);
    };
    dashboardsApi
      .data(dashboard.id)
      .then((cached) => {
        if (cancelled) return;
        if (cached.length > 0) {
          applyResults(cached);
          const newest = cached
            .map((item) => item.provenance?.executedAt)
            .filter((value): value is string => Boolean(value))
            .sort()
            .pop();
          if (newest) setRefreshedAt(newest);
        }
        runMissing(
          new Set([
            ...seededIds.current,
            ...cached.map((item) => item.panelId),
          ]),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setNotice("Saved results could not be loaded. Try refreshing.");
        runMissing(seededIds.current);
      });
    return () => {
      cancelled = true;
    };
    // Once per dashboard: re-running on every refresh identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard.id]);

  async function exportAs(format: ExportFormat, mode: ExportMode) {
    setChoosingFor("");
    setExporting(format);
    setNotice("");
    try {
      // Only the PDF embeds screen images. The deck builds native PowerPoint
      // charts server-side from the same rows so a recipient can edit them.
      const images = format === "pdf" ? captureCharts(panelIds) : [];
      const blob = await dashboardsApi.export(
        dashboard.id,
        format,
        mode,
        images,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${dashboard.name || "dashboard"}.${format}`;
      link.click();
      // Revoked on the next frame: revoking synchronously can cancel the
      // download before the browser has read the blob.
      requestAnimationFrame(() => URL.revokeObjectURL(url));

      devTrace.record({
        runId: dashboard.id,
        step: "dashboard_exported",
        actor: "Dashboard",
        level: "success",
        summary: `Exported ${dashboard.name} as ${format.toUpperCase()}.`,
        output: { format, mode, images: images.length },
      });
    } catch (error) {
      setNotice(
        error instanceof ApiError
          ? error.message
          : "The export could not be produced.",
      );
    } finally {
      setExporting("");
    }
  }

  const prepareEmail = useCallback((): EmailDraftSource => {
    const images = captureCharts(panelIds);
    return {
      document: dashboardEmailDocument({ dashboard, results, images }),
      attachment: async () =>
        pdfAttachment(
          await dashboardsApi.export(dashboard.id, "pdf", "executive", images),
          dashboard.name || "dashboard",
        ),
    };
  }, [dashboard, panelIds, results]);

  const failedCount = [...results.values()].filter(
    (item) => item.status === "error",
  ).length;
  const endorsement = dashboardEndorsementLabel(results.values());
  const periodNotes = dashboardPeriodNotes(results.values());

  return (
    <article className="dashboard-view">
      <header className="dashboard-view-head">
        <div className="dashboard-view-title">
          <h2>{dashboard.name}</h2>
          <p className="dashboard-view-meta">
            <span className={`status-pill ${dashboard.status}`}>
              {dashboard.status === "draft" ? "Draft" : "Published"}
            </span>
            <span>
              {dashboard.panels.length} panel
              {dashboard.panels.length === 1 ? "" : "s"}
            </span>
            {dashboard.contextId && (
              <span className="context-badge" title="Governed context">
                {dashboard.contextId}
              </span>
            )}
            {refreshedAt && (
              // The dot is what turns a timestamp into a sign of life. Amber
              // once the figures are old enough that a reader should refresh
              // before quoting them.
              <span
                className={`freshness ${isStale(refreshedAt) ? "stale" : ""}`}
              >
                <i aria-hidden="true" />
                updated {relativeTime(refreshedAt)}
              </span>
            )}
          </p>
        </div>

        <div className="dashboard-toolbar">
          <button
            type="button"
            className="button secondary"
            onClick={() => void refresh()}
            disabled={refreshing || dashboard.panels.length === 0}
          >
            <RefreshCw size={16} className={refreshing ? "spinning" : ""} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>

          <button
            type="button"
            className="button tertiary"
            onClick={() => setChoosingFor("pdf")}
            disabled={Boolean(exporting)}
            aria-haspopup="menu"
            aria-expanded={choosingFor === "pdf"}
          >
            {exporting === "pdf" ? (
              <Download size={16} className="spinning" />
            ) : (
              <FileText size={16} />
            )}
            PDF
          </button>
          <button
            type="button"
            className="button tertiary"
            onClick={() => setChoosingFor("pptx")}
            disabled={Boolean(exporting)}
            aria-haspopup="menu"
            aria-expanded={choosingFor === "pptx"}
          >
            {exporting === "pptx" ? (
              <Download size={16} className="spinning" />
            ) : (
              <Presentation size={16} />
            )}
            PowerPoint
          </button>

          <EmailButton
            traceId={dashboard.id}
            kind="dashboard"
            prepare={prepareEmail}
            disabled={Boolean(exporting) || dashboard.panels.length === 0}
            disabledReason="A dashboard without panels has nothing to email"
          />

          {choosingFor ? (
            <ExportAudienceChooser
              format={choosingFor}
              onChoose={(mode) => void exportAs(choosingFor, mode)}
              onCancel={() => setChoosingFor("")}
            />
          ) : null}
        </div>
      </header>

      {endorsement && (
        <p className="dashboard-governance">Semantic model: {endorsement}</p>
      )}
      {periodNotes.length > 0 && (
        <ul className="receipt-period-notes" aria-label="Incomplete periods">
          {periodNotes.map((note) => (
            <li key={note}>
              <CalendarClock size={14} aria-hidden="true" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
      )}

      {notice && (
        <p className="inline-notice warning" role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          {notice}
          {failedCount > 0 && (
            <button
              type="button"
              className="button tertiary"
              onClick={() =>
                void refresh(
                  [...results.values()]
                    .filter((item) => item.status === "error")
                    .map((item) => item.panelId),
                )
              }
            >
              Retry failed panels
            </button>
          )}
        </p>
      )}

      {dashboard.panels.length === 0 ? (
        <div className="dashboard-blank">
          <ChartColumnBig size={26} aria-hidden="true" />
          <p>This dashboard has no panels yet. Add a saved chart from your tray.</p>
        </div>
      ) : (
        <div className="dashboard-grid">
          {dashboard.panels.map((panel) => (
            <DashboardPanel
              key={panel.id}
              panel={panel}
              result={results.get(panel.id)}
              busy={refreshing || busyPanels.has(panel.id)}
              onRetry={() => void refresh([panel.id])}
            />
          ))}
        </div>
      )}

      {onRenamed && dashboard.status === "draft" && (
        <footer className="dashboard-view-foot">
          {/* Auto-saved dashboards land as drafts so an exploratory question
              that happened to draw charts does not clutter a curated list. */}
          <button
            type="button"
            className="button primary"
            onClick={() => {
              void dashboardsApi
                .update(dashboard.id, { status: "published" })
                .then(onRenamed)
                .catch(() => setNotice("This dashboard could not be published."));
            }}
          >
            Publish to my workspace
          </button>
        </footer>
      )}
    </article>
  );
}
